import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useVault } from "./VaultContext";

const Ctx = createContext(null);

function isSameLocalDay(a, b) {
  if (!a || !b) return false;
  const A = new Date(a);
  const B = new Date(b);
  return A.getFullYear() === B.getFullYear() && A.getMonth() === B.getMonth() && A.getDate() === B.getDate();
}
function isYesterday(a, b = new Date()) {
  if (!a) return false;
  const Y = new Date(b);
  Y.setDate(Y.getDate() - 1);
  return isSameLocalDay(a, Y);
}

export function StreaksProvider({ children }) {
  const { ready, getJSON, setJSON } = useVault();
  const [state, setState] = useState({ current: 0, best: 0, lastCheckIn: null });

  // One-time migration from localStorage → vault (if any)
  useEffect(() => {
    if (!ready) return;
    (async () => {
      const existing = await getJSON("streaks", null);
      if (existing) { setState(existing); return; }

      try {
        const raw = localStorage.getItem("mudo:streak");
        if (raw) {
          const parsed = JSON.parse(raw);
          await setJSON("streaks", parsed);
          localStorage.removeItem("mudo:streak");
          setState(parsed);
          window.dispatchEvent(new Event("mudo:streak-updated"));
          return;
        }
      } catch {}

      // No prior data—seed
      await setJSON("streaks", { current: 0, best: 0, lastCheckIn: null });
      setState({ current: 0, best: 0, lastCheckIn: null });
    })();
  }, [ready, getJSON, setJSON]);

  // Reload after a Receive/Restore
  useEffect(() => {
    if (!ready) return;
    const onRestore = async () => {
      const fresh = await getJSON("streaks", { current: 0, best: 0, lastCheckIn: null });
      setState(fresh);
    };
    window.addEventListener("mudo:vault-restored", onRestore);
    return () => window.removeEventListener("mudo:vault-restored", onRestore);
  }, [ready, getJSON]);

  const save = useCallback(async (next) => {
    await setJSON("streaks", next);
    setState(next);
    window.dispatchEvent(new Event("mudo:streak-updated"));
  }, [setJSON]);

  const checkIn = useCallback(async () => {
    if (!ready) return;
    const nowIso = new Date().toISOString();
    const last = state.lastCheckIn;

    // Already done today → no-op
    if (isSameLocalDay(last, new Date())) return;

    const nextCurrent = isYesterday(last) ? (Number(state.current || 0) + 1) : 1;
    const next = {
      current: nextCurrent,
      best: Math.max(Number(state.best || 0), nextCurrent),
      lastCheckIn: nowIso,
    };
    await save(next);
  }, [ready, state, save]);

  const reset = useCallback(async () => {
    if (!ready) return;
    await save({ current: 0, best: Number(state.best || 0), lastCheckIn: null });
  }, [ready, state, save]);

  // Auto-check-in when the Journal logs a new entry for today
  useEffect(() => {
    const onNewEntry = async (e) => {
      const dateKey = e?.detail?.date; // YYYY-MM-DD
      if (!dateKey) return;
      const todayKey = new Date().toISOString().slice(0, 10);
      if (dateKey === todayKey) checkIn();
    };
    window.addEventListener("mudo:journal-entry-created", onNewEntry);
    return () => window.removeEventListener("mudo:journal-entry-created", onNewEntry);
  }, [checkIn]);

  const value = useMemo(() => ({
    current: Number(state.current || 0),
    best: Number(state.best || 0),
    lastCheckIn: state.lastCheckIn || null,
    checkIn,
    reset,
  }), [state, checkIn, reset]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStreaks() {
  return useContext(Ctx);
}
